<template>
  <SCADropdown
    v-if="canCreateAnyTemplate"
    v-model:is-open="isOpen"
    :placement="placement"
    :offset-x="offsetX"
    :offset-y="offsetY"
    toolbar-offset
    no-padding
    scroll-down
    scroll-up
    @opened="handleOpened"
    @positioned="focusSearchInput"
  >
    <template #trigger="{ target, togglePopover }">
      <div ref="triggerElRef">
        <div
          v-if="slots['default']"
          @pointerdown="wasOpenBeforePointerdown = isOpen"
          @click.stop.prevent="togglePopover({ force: !wasOpenBeforePointerdown })"
        >
          <slot />
        </div>
        <template v-else>
          <SCAButton
            v-if="buttonLabel"
            size="small"
            :popovertarget="target"
            appearance="secondary"
            icon-left="phosphor/plus"
            class="cursor-pointer"
            :disabled="previewMode || !canCreateRecordsByModuleTag(module?.builtinModule)"
          >
            {{ buttonLabel }}
          </SCAButton>
          <SCASplitButton
            v-else
            :active="isOpen"
            :disabled="previewMode"
            :left-disabled="
              previewMode ||
              !templateId ||
              backToRoute ||
              !canCreateRecordsByModuleTag(module?.builtinModule) ||
              isSelectedTemplateRestricted
            "
            :right-disabled="previewMode || !canCreateRecordsByModuleTag(module?.builtinModule)"
            :popover-target="target"
            :title="$t('common.action.createNew')"
            @left-click="onCreateClick"
          />
        </template>
      </div>
    </template>
    <template #default="{ popover }">
      <div :class="$style.contentWrapper" :style="dynamicStyles">
        <div :class="$style.searchWrapper">
          <SCASearchInput
            :key="'search'"
            appearance="border"
            :class="$style.searchInput"
            :show-search-icon="true"
            :show-clear-icon="searchTerm.length > 0"
            @clear="clearSearch"
          >
            <input ref="searchInputRef" v-model="searchTerm" :placeholder="$t('common.action.search')" />
          </SCASearchInput>
          <SCADivider appearance="border" :class="$style.divider" />
        </div>
        <div :class="$style.scrollableContent">
          <SCAStateBox :has-error="!!templatesError">
            <template v-for="item in topLevelItems" :key="item.data.id">
              <template v-if="item.type === 'template'">
                <template v-if="canCreate(getTemplatePermissions(item.data.id))">
                  <SCASidebarNavigationItem
                    v-if="onItemClick"
                    :label="'name' in item.data ? item.data.name : ''"
                    :active="false"
                    :disabled="false"
                    is-mini
                    @click="
                      () => {
                        onItemClick!(item.data as TemplateInfo);
                        clearSearch();
                        popover?.hidePopover();
                      }
                    "
                  />
                  <RouterLink
                    v-else
                    :to="{
                      name: Routes.RECORD_CREATE,
                      params: { moduleId: props.moduleId, templateId: item.data.id },
                      query: {
                        ...route.query,
                        ...(routeRecordId && routeModuleId && returnTo && backToRoute
                          ? {
                              implicitRelationsId: routeRecordId,
                              implicitRelationsModuleId: routeModuleId,
                              returnTo: returnTo,
                            }
                          : {}),
                      },
                    }"
                    @click="
                      () => {
                        clearSearch();
                        popover?.hidePopover();
                      }
                    "
                  >
                    <SCASidebarNavigationItem
                      :label="'name' in item.data ? item.data.name : ''"
                      :active="false"
                      :disabled="false"
                      is-mini
                    />
                  </RouterLink>
                </template>
              </template>
              <template v-else>
                <NavTree
                  :nav="mapFolderToNavNode(item.data as ModuleFolderStructureItem)"
                  :level="0"
                  :is-create-mode="true"
                  :is-search-active="!!searchTerm"
                  :search-term="searchTerm"
                  :on-item-click="onItemClick"
                  is-mini
                  @close="
                    () => {
                      clearSearch();
                      popover?.hidePopover();
                    }
                  "
                />
              </template>
            </template>
          </SCAStateBox>
        </div>
      </div>
    </template>
  </SCADropdown>
</template>
<script setup lang="ts">
import type { ModuleFolderStructureItem, PermissionCRUDH, TemplateInfo } from "@scayla/api-client-fetch";
import { SidebarStatus } from "@scayla/api-client-fetch/types";
import {
  SCAButton,
  SCADivider,
  SCADropdown,
  SCASearchInput,
  SCASidebarNavigationItem,
  SCASplitButton,
  SCAStateBox,
} from "@scayla/components";
import { useQuery } from "@tanstack/vue-query";
import { computed, ref, useSlots, useTemplateRef } from "vue";
import { useRoute, useRouter } from "vue-router";

import NavTree from "@/components/navigation/NavTree.vue";
import { getModuleFoldersStructureQuery } from "@/composables/useModuleFoldersStructure";
import { Routes } from "@/router";
import { useWorkspace } from "@/store/workspace";
import { canCreateRecordsByModuleTag } from "@/utils/moduleUtils";
import type { NavNode } from "@/utils/navUtils";
import { useTriggerAvailableHeight } from "@/views/record/composables/useTriggerAvailableHeight";
import { collectModuleTemplates } from "@/views/record/helpers/moduleTemplates";

const props = withDefaults(
  defineProps<{
    moduleId: string;
    buttonLabel?: string;
    showAllTemplates?: boolean;
    backToRoute?: boolean;
    systemTemplateSlugFilter?: string;
    templateId?: string;
    previewMode?: boolean;
    placement?: (typeof SCADropdown)["placement"];
    offsetX?: number;
    offsetY?: number;
    onItemClick?: (template: TemplateInfo) => void;
  }>(),
  {
    buttonLabel: undefined,
    showAllTemplates: true,
    backToRoute: false,
    systemTemplateSlugFilter: undefined,
    templateId: undefined,
    previewMode: false,
    placement: "bottom-start",
    offsetX: undefined,
    offsetY: undefined,
    onItemClick: undefined,
  },
);

const isOpen = defineModel<boolean>("isOpen", { default: false });

const slots = useSlots();
const workspace = useWorkspace();
const module = computed(() => workspace.modules?.find((m) => m.id === props.moduleId));
const searchTerm = ref("");
const isSearchActive = ref(false);
const wasOpenBeforePointerdown = ref(false);
const searchInputRef = useTemplateRef<HTMLInputElement>("searchInputRef");
const route = useRoute();
const router = useRouter();

const { triggerElRef, style: dynamicStyles, calculate: calculateContentMaxHeight } = useTriggerAvailableHeight();

function handleOpened() {
  calculateContentMaxHeight(props.placement as string, props.offsetY);
}

function focusSearchInput() {
  searchInputRef.value?.focus({ preventScroll: true });
}

const routeModuleId = computed(() => route.params?.moduleId as string | undefined);
const routeRecordId = computed(() => route.params?.recordId as string | undefined);
const templateId = computed(() => props.templateId);
const isSelectedTemplateRestricted = computed(() => {
  if (!props.templateId || !foldersStructure.value) return false;
  const selected = collectModuleTemplates(foldersStructure.value).find((t) => t.id === props.templateId);
  return selected?.status === SidebarStatus.LOCKED || selected?.status === SidebarStatus.DRAFT;
});
const returnTo = computed(
  () =>
    router.resolve({
      name: Routes.RECORD,
      params: {
        moduleId: routeModuleId.value,
        recordId: routeRecordId.value,
      },
      query: route.query,
    }).fullPath,
);

function clearSearch() {
  isSearchActive.value = false;
  searchTerm.value = "";
}

async function navigateToCreateTemplate() {
  if (!templateId.value) {
    return;
  }
  await router.push({
    name: Routes.RECORD_CREATE,
    params: { moduleId: props.moduleId, templateId: templateId.value },
    query: { ...route.query },
  });
}

function onCreateClick() {
  // Prevent create for implicit relations-backToRoute
  if (!templateId.value || props.backToRoute) {
    return;
  }
  void navigateToCreateTemplate();
}

const foldersQuery = computed(() => {
  const base = getModuleFoldersStructureQuery(props.moduleId);

  return {
    ...base,
    enabled: base.enabled && !props.previewMode,
  };
});

const { data: foldersStructure, error: templatesError } = useQuery(foldersQuery);

// Cache search term processing to avoid repeated toLowerCase operations
const lowerSearchTerm = computed(() => searchTerm.value.toLowerCase());

// Use workspace store's cached template permissions - no local caching needed
const getCachedTemplatePermissions = workspace.getTemplatePermissions;

const topLevelItems = computed(() => {
  var templateItems = getModuleTemplates.value.map((t) => ({ type: "template", data: t }));
  if (props.systemTemplateSlugFilter) {
    templateItems = templateItems.filter((t) => t.data.id === props.systemTemplateSlugFilter);
  }
  const folderItems = getModuleFolders.value.map((f) => ({ type: "folder", data: f })) ?? [];

  const allItems = [...templateItems, ...folderItems];
  allItems.sort((a, b) => a.data.indexId - b.data.indexId);
  return allItems;
});

const mapFolderToNavNode = (folder: ModuleFolderStructureItem): NavNode => {
  const children: NavNode[] = [];

  // Process subfolders recursively
  if (folder.folders?.length) {
    children.push(...folder.folders.map(mapFolderToNavNode));
  }

  // Process templates with cached permissions
  if (folder.templates?.length) {
    for (const template of folder.templates) {
      if (template.status === SidebarStatus.LOCKED || template.status === SidebarStatus.DRAFT) continue;

      const permissions = getCachedTemplatePermissions(template.id);

      // Early exit if no read permission
      if (!permissions.read) continue;

      // Check showAllTemplates or showInNavigation
      if (!props.showAllTemplates && !template.showInNavigation) continue;

      children.push({
        label: template.name,
        layoutStoreKey: template.id + "-create",
        actions: false,
        disabled: !permissions.create,
        ...(permissions.create && props.onItemClick
          ? {
              onItemClick: () => props.onItemClick!(template),
            }
          : {
              route: {
                name: Routes.RECORD_CREATE,
                params: { moduleId: props.moduleId, templateId: template.id },
                query:
                  routeRecordId.value && routeModuleId.value && returnTo.value && props.backToRoute
                    ? {
                        implicitRelationsId: routeRecordId.value,
                        implicitRelationsModuleId: routeModuleId.value,
                        returnTo: returnTo.value,
                      }
                    : {},
              },
            }),
        indexId: template.indexId,
      });
    }
  }

  return {
    label: folder.label,
    layoutStoreKey: folder.id + "-create",
    children,
    indexId: folder.indexId,
  };
};

// Lazy computed folder structures - only process when open
const getModuleFolders = computed(() => {
  if (!isOpen.value) return [];

  const folders = foldersStructure.value?.folders ?? [];

  // Only include folders with templates or subfolders
  const filteredFolders = folders.filter((folder) => {
    const templatesCount = folder.templates?.length ?? 0;
    const folderCount = folder.folders?.length ?? 0;
    return templatesCount > 0 || folderCount > 0;
  });

  return filteredFolders;
});

// Optimized template filtering with cached search term
const getModuleTemplates = computed(() => {
  if (!isOpen.value) return [];

  const templates = foldersStructure.value?.templates;
  if (!templates?.length) return [];

  let filteredTemplates = templates;

  // Filter by permissions first (most expensive operation)
  if (props.showAllTemplates) {
    filteredTemplates = templates.filter(
      (t: TemplateInfo) =>
        t.status !== SidebarStatus.LOCKED &&
        t.status !== SidebarStatus.DRAFT &&
        getCachedTemplatePermissions(t.id).read,
    );
  } else {
    filteredTemplates = templates.filter(
      (t: TemplateInfo) =>
        t.status !== SidebarStatus.LOCKED &&
        t.status !== SidebarStatus.DRAFT &&
        getCachedTemplatePermissions(t.id).read &&
        t.showInNavigation,
    );
  }

  // Then filter by search term using cached lowercase
  if (lowerSearchTerm.value) {
    filteredTemplates = filteredTemplates.filter((t: TemplateInfo) =>
      t.name.toLowerCase().includes(lowerSearchTerm.value),
    );
  }

  return filteredTemplates;
});

// Cached template creation capability check
const canCreateAnyTemplate = computed(
  () =>
    foldersStructure.value &&
    collectModuleTemplates(foldersStructure.value).some((t: TemplateInfo) =>
      canCreate(getCachedTemplatePermissions(t.id)),
    ),
);

// ------------
// Permissions
// ------------
const getTemplatePermissions = workspace.getTemplatePermissions;

function canCreate(permissions: PermissionCRUDH) {
  return permissions.read && permissions.create;
}
</script>
<style lang="scss" module>
.searchInput {
  display: flex;
  align-items: center;
  height: 28px;
  max-height: 28px;
  width: 100%;
  margin-bottom: 7px;
}

.divider {
  width: calc(100% + 24px);
  margin: 7px -12px 12px !important;
}

.contentWrapper {
  display: flex;
  flex-direction: column;
  width: calc(241px + 8px);
  padding: 0 var(--space-component-padding-xSmall);
  max-height: 400px;
}

.searchWrapper {
  position: sticky;
  top: 0;
}

.scrollableContent {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}
</style>
